/**
 * Geolocalizzazione utente + calcolo distanza lato frontend.
 * Salva la posizione in localStorage per non richiedere ogni volta.
 */

export function getUserLocation() {
  return new Promise((resolve, reject) => {
    // Prima controlla cache (valida 30 minuti)
    const cached = localStorage.getItem('user_location');
    if (cached) {
      const data = JSON.parse(cached);
      const age = Date.now() - data.timestamp;
      if (age < 30 * 60 * 1000) {
        resolve(data);
        return;
      }
    }

    if (!navigator.geolocation) {
      reject(new Error('Geolocalizzazione non supportata'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const data = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          timestamp: Date.now(),
        };
        localStorage.setItem('user_location', JSON.stringify(data));
        resolve(data);
      },
      (err) => reject(err),
      { timeout: 8000, maximumAge: 1800000 }
    );
  });
}

export function getCachedLocation() {
  const cached = localStorage.getItem('user_location');
  if (!cached) return null;
  const data = JSON.parse(cached);
  const age = Date.now() - data.timestamp;
  if (age > 30 * 60 * 1000) return null;
  return data;
}

export function clearLocation() {
  localStorage.removeItem('user_location');
}

/**
 * Haversine formula - distanza in km tra due coordinate
 */
export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Database CAP svizzeri principali (lat, lng)
 * Stessa mappa del backend per calcolo client-side istantaneo
 */
const SWISS_PLZ = {
  '1000': [46.5197, 6.6323], '1200': [46.2044, 6.1432], '1700': [46.8065, 7.1620],
  '1800': [46.4497, 6.8433], '1820': [46.4338, 6.9127], '1950': [46.2330, 7.3603],
  '2000': [47.0030, 6.9430], '2300': [47.0910, 6.7970], '2500': [47.1365, 7.2461],
  '3000': [46.9481, 7.4474], '3001': [46.9481, 7.4474], '3006': [46.9481, 7.4474],
  '3007': [46.9481, 7.4474], '3008': [46.9481, 7.4474], '3010': [46.9481, 7.4474],
  '3011': [46.9481, 7.4474], '3012': [46.9481, 7.4474], '3018': [46.9481, 7.4474],
  '3027': [46.9481, 7.4474], '3400': [47.0000, 7.7381], '3600': [46.7526, 7.6267],
  '3800': [46.6862, 7.8632], '4000': [47.5596, 7.5886], '4001': [47.5596, 7.5886],
  '4051': [47.5596, 7.5886], '4052': [47.5596, 7.5886], '4500': [47.2088, 7.5358],
  '5000': [47.3925, 8.0440], '5400': [47.4834, 8.2101], '6000': [47.0502, 8.3093],
  '6002': [47.0502, 8.3093], '6003': [47.0502, 8.3093], '6004': [47.0502, 8.3093],
  '6006': [47.0502, 8.3093], '6300': [47.1734, 8.5153], '6500': [46.1964, 8.9956],
  '6600': [46.1289, 8.7962], '6900': [46.0037, 8.9511], '6901': [46.0037, 8.9511],
  '7000': [46.8508, 9.5322], '7001': [46.8508, 9.5322], '8000': [47.3769, 8.5417],
  '8001': [47.3769, 8.5417], '8002': [47.3769, 8.5417], '8003': [47.3769, 8.5417],
  '8004': [47.3769, 8.5417], '8005': [47.3769, 8.5417], '8006': [47.3769, 8.5417],
  '8008': [47.3769, 8.5417], '8032': [47.3769, 8.5417], '8044': [47.3769, 8.5417],
  '8045': [47.3769, 8.5417], '8050': [47.3769, 8.5417], '8051': [47.3769, 8.5417],
  '8052': [47.3769, 8.5417], '8053': [47.3769, 8.5417], '8055': [47.3769, 8.5417],
  '8057': [47.3769, 8.5417], '8304': [47.4361, 8.7294], '8305': [47.4361, 8.7294],
  '8400': [47.4994, 8.7231], '8401': [47.4994, 8.7231], '8402': [47.4994, 8.7231],
  '8600': [47.3954, 8.7289], '9000': [47.4239, 9.3748], '9001': [47.4239, 9.3748],
  '9200': [47.5500, 9.0367], '9400': [47.5631, 9.7394], '9490': [47.1667, 9.5167],
  '9500': [47.5500, 9.3167],
};

export function getDistanceFromUser(userLat, userLng, postalCode) {
  if (!userLat || !userLng || !postalCode) return null;
  const plz = String(postalCode).substring(0, 4);
  const coords = SWISS_PLZ[plz];
  if (!coords) return null;
  return Math.round(haversineKm(userLat, userLng, coords[0], coords[1]));
}
